import { DropdownMenuItem } from '@synoptic/ui-kit/dropdown-menu/dropdown-menu.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@synoptic/ui-kit/tooltip/tooltip.js';
import { clsx } from 'clsx';
import {
  ComponentPropsWithRef,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useState,
} from 'react';
import { To, useLocation, useResolvedPath } from 'react-router';
import { Link, LinkProps } from '@/routes-utils/navigation.tsx';
import {
  navBadge,
  navItem,
  navItemActive,
  navItemBar,
  navItemLabel,
  navList,
  navSection,
  navSectionLabel,
  navSubItemActive,
  navSubItemDot,
  navSubMenu,
  navTooltipArrow,
  navTooltipContent,
} from './nav-item-v2.css.ts';
import { NavBadgeV2 } from './nav-items-v2.tsx';
import { NavVariantV2, useNavVariantV2 } from './nav-variant-v2.ts';

type NavItemV2CommonProps = {
  icon: ReactNode;
  label: string;
  badge?: NavBadgeV2;
  className?: string;
};

const isBar = (variant: NavVariantV2) => variant === 'bar';

const useIsLinkActive = (to: To) => {
  const pathname = useLocation().pathname.toLowerCase();
  const target = useResolvedPath(to).pathname.toLowerCase();

  return (
    pathname === target ||
    (pathname.startsWith(target) && pathname.charAt(target.length) === '/')
  );
};

const keepMenuOpen = (event: Event) => event.preventDefault();

const SidebarTooltip = ({
  label,
  enabled,
  children,
}: {
  label: string;
  enabled: boolean;
  children: ReactElement;
}) => {
  const [open, setOpen] = useState(false);
  if (!enabled && open) {
    setOpen(false);
  }

  return (
    <Tooltip
      disableHoverableContent
      open={enabled && open}
      onOpenChange={(next) => setOpen(enabled && next)}
    >
      <TooltipTrigger asChild onClick={undefined}>
        {children}
      </TooltipTrigger>
      <TooltipContent
        color="prominent"
        side="right"
        className={navTooltipContent}
        arrowClassName={navTooltipArrow}
        description={label}
      />
    </Tooltip>
  );
};

const withVariantWrapper = (
  variant: NavVariantV2,
  label: string,
  element: ReactElement,
  closeMenuOnSelect = true,
) => {
  switch (variant) {
    case 'expanded':
    case 'rail':
      return (
        <SidebarTooltip label={label} enabled={variant === 'rail'}>
          {element}
        </SidebarTooltip>
      );
    case 'menu':
      return (
        <DropdownMenuItem
          asChild
          onSelect={closeMenuOnSelect ? undefined : keepMenuOpen}
        >
          {element}
        </DropdownMenuItem>
      );
    case 'bar':
      return element;
    default:
      return variant satisfies never;
  }
};

const NavItemContent = ({
  icon,
  label,
  badge,
  variant,
  children,
}: PropsWithChildren<
  Pick<NavItemV2CommonProps, 'icon' | 'label' | 'badge'> & {
    variant: NavVariantV2;
  }
>) => (
  <>
    {icon}
    <span className={isBar(variant) ? undefined : navItemLabel}>{label}</span>
    {badge && !isBar(variant) ? (
      <span className={navBadge}>{badge}</span>
    ) : null}
    {children}
  </>
);

type NavLinkItemV2Props = PropsWithChildren<NavItemV2CommonProps> &
  Omit<LinkProps, 'className' | 'children'> & {
    activePrefix?: string;
  };

export const NavLinkItemV2 = ({
  icon,
  label,
  badge,
  className,
  children,
  activePrefix,
  ...props
}: NavLinkItemV2Props) => {
  const variant = useNavVariantV2();
  const isActive = useIsLinkActive(activePrefix ?? props.to);

  return withVariantWrapper(
    variant,
    label,
    <Link
      prefetch="render"
      aria-current={isActive ? 'page' : undefined}
      className={clsx(
        navItem,
        { [navItemBar]: isBar(variant), [navItemActive]: isActive },
        className,
      )}
      {...props}
    >
      <NavItemContent icon={icon} label={label} badge={badge} variant={variant}>
        {children}
      </NavItemContent>
    </Link>,
  );
};

export type NavButtonItemV2Props = PropsWithChildren<NavItemV2CommonProps> &
  Omit<ComponentPropsWithRef<'button'>, 'className' | 'children'> & {
    active?: boolean;
    closeMenuOnSelect?: boolean;
  };

export const NavButtonItemV2 = ({
  icon,
  label,
  badge,
  active,
  className,
  children,
  closeMenuOnSelect = true,
  ...props
}: NavButtonItemV2Props) => {
  const variant = useNavVariantV2();

  return withVariantWrapper(
    variant,
    label,
    <button
      type="button"
      className={clsx(
        navItem,
        { [navItemBar]: isBar(variant), [navItemActive]: active },
        className,
      )}
      {...props}
    >
      <NavItemContent icon={icon} label={label} badge={badge} variant={variant}>
        {children}
      </NavItemContent>
    </button>,
    closeMenuOnSelect,
  );
};

export const NavSectionV2 = ({
  label,
  children,
}: PropsWithChildren<{ label?: string }>) => (
  <section className={navSection} aria-label={label}>
    {label ? <div className={navSectionLabel}>{label}</div> : null}
    <div className={navList}>{children}</div>
  </section>
);

export const NavListV2 = ({
  className,
  children,
}: PropsWithChildren<{ className?: string }>) => (
  <div className={clsx(navList, className)}>{children}</div>
);

export const NavSubMenuV2 = ({ children }: PropsWithChildren) => (
  <div className={navSubMenu}>{children}</div>
);

type NavSubItemV2Props = {
  label: string;
  active: boolean;
} & Omit<LinkProps, 'className' | 'children'>;

export const NavSubItemV2 = ({
  label,
  active,
  ...props
}: NavSubItemV2Props) => (
  <Link
    prefetch="render"
    aria-current={active ? 'page' : undefined}
    className={clsx(navItem, { [navSubItemActive]: active })}
    {...props}
  >
    <span className={navItemLabel}>{label}</span>
    {active ? <span className={navSubItemDot} /> : null}
  </Link>
);
